import { action, observable, makeObservable } from 'mobx';

import type { EntryTypeValueData, EntryTypeValueTemplateData } from 'chaika-frontend-types/frontend-cms';

import { SimpleList } from '@store/models/SimpleList';
import { assertIsDefined } from '@utils/assertIsDefined';

export interface EntryTypeCommonTemplateListFilterParams {
    name?: string;
}

export class EntryTypeCommonTemplateList extends SimpleList<
    EntryTypeValueData,
    EntryTypeCommonTemplateListFilterParams
> {
    @observable
    private openTemplateIds = new Set<string>();

    constructor(params: EntryTypeValueData[] = []) {
        super(params);
        makeObservable(this);

        params.forEach(({ id }) => {
            this.openTemplateIds.add(id);
        });
    }

    public hasTemplates(): boolean {
        return this.getSize() > 0;
    }

    public getFieldTemplates(id: string): EntryTypeValueTemplateData[] {
        return this.get(id)?.templates || [];
    }

    public getFieldTemplate(id: string, templateId: string): EntryTypeValueTemplateData {
        const template = this.getFieldTemplates(id).find(({ id }) => id === templateId);
        assertIsDefined(template, `Template ${templateId} is one of the common templates of ${id}`);
        return template;
    }

    public getFieldTemplatesIds(id: string): string[] {
        return this.getFieldTemplates(id).map(({ id }) => id);
    }

    public getFieldTemplatesNames(id: string): string[] {
        return this.getFieldTemplates(id).map(({ fieldName }) => {
            assertIsDefined(fieldName, 'Common field templates are created for a field');
            return fieldName;
        });
    }

    public isTemplateOpen(templateId: string): boolean {
        return this.openTemplateIds.has(templateId);
    }

    @action
    public toggleIsTemplateOpen(id: string): void {
        if (this.openTemplateIds.has(id)) {
            this.openTemplateIds.delete(id);
        } else {
            this.openTemplateIds.add(id);
        }
    }

    @action
    public openAllFieldTemplate(): void {
        this.ids.forEach(id => {
            this.openTemplateIds.add(id);
        });
    }

    @action
    public closeAllFieldTemplate(): void {
        this.openTemplateIds.clear();
    }

    @action
    public updateFieldTemplate(id: string, templateId: string, params: Partial<EntryTypeValueTemplateData>): void {
        const templates = this.getFieldTemplates(id).map(template =>
            template.id === templateId ? { ...template, ...params } : template,
        );

        this.update(id, { templates });
    }

    @action
    public addFieldTemplate(id: string, newTemplate: EntryTypeValueTemplateData): void {
        this.update(id, {
            templates: [...this.getFieldTemplates(id), newTemplate],
        });
    }

    @action
    public setFieldTemplates(id: string, templates: EntryTypeValueTemplateData[]): void {
        this.update(id, { templates });
    }

    @action
    public removeFieldTemplate(id: string, templateId: string): void {
        const templates = this.getFieldTemplates(id).filter(template => template.id !== templateId);

        this.update(id, { templates });
    }

    protected filterValue(value: EntryTypeValueData, { name }: EntryTypeCommonTemplateListFilterParams): boolean {
        return this.filterFieldValueIncludesString(value, name, 'name');
    }
}
